// src/screens/admin/SuperAdminScreen.js
// 총관리자 대시보드 화면 — 대시보드 전용
// 27차: 수수료·정산·신고 탭 제거 (→ SuperAdminStoresScreen / SuperAdminUserReportScreen으로 통합)
// 28차: 총 회원 드릴다운 — 총관리자 → 매장관리자 → 일반 사용자 순서 정렬
// 51차: 색/폰트 theme.js 단일화 — onGold 토큰, 'serif'→FONTS.display
// 75차: maxThemes 변경 시 stores/{id}/contractHistory에 이력 기록 추가
// 76차: 계약 테마 수 수정 모달의 커스텀 제목 텍스트 → 공통 AppHeader(isModal)로 교체
// 76차: 대시보드 우측 상단 로그아웃 버튼 제거 — 마이 탭(MyPageScreen.js)에 이미 있어 중복
// 77차: design 폴더(design_handoff_unlock) "총관리자 콘솔" 스펙 부분 적용 — 이 화면은 이미
// theme.js 토큰(COLORS/RADIUS/SHADOW)을 그대로 쓰고 있어 색/타이포는 스펙과 대부분 일치.
// 실제로 다른 건 카드 radius/헤드라인 카드 강조 정도 — KPI 카드 radius를 16(spec)로,
// 가장 핵심 지표(이달 예약)를 골드 틴트 헤드라인 카드로 구분. 스펙 목업의 "매장 승인 대기",
// "매장 정산 상위 랭킹"은 여전히 미구현(매장 승인 워크플로우·정산 랭킹 뷰 자체가 없음).
// 78차 32차: "이 달 GMV"는 실제로 구현 — 아래 GMV 대시보드 카드 참고(전체 reservations를
// 직접 집계, paymentStatus:'pending' 제외 — StoreAdminScreen과 동일 기준).

import React, { useState, useEffect, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  RefreshControl, TextInput, Modal,
} from 'react-native';
import {
  collection, getDocs, doc, updateDoc, query, orderBy, limit, where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { logContractChange, invalidateStoreCache } from '../../services/productService';
import { getStoreThemeCount } from '../../services/feeService';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader, { RoleBadge } from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton } from '../../components/common/Buttons';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import {
  BuildingIcon, MenuStoreIcon, MenuEscapeIcon, MenuReservationIcon, PeopleIcon,
  AdminIcon, MenuNicknameIcon, IconLabel, PriceIcon, MenuBellIcon, ContractIcon,
  ContractExpiringIcon, WarningIcon,
} from '../../components/common/Icons';
import { COLORS, RADIUS, SHADOW, FONTS } from '../../constants/theme';
import { thisMonthStr, todayStr } from '../../utils/dateUtils';

const C = {
  bg:      COLORS.bg,
  surface: COLORS.surface,
  card:    COLORS.surface2,
  border:  COLORS.border,
  gold:    COLORS.gold,
  goldBg:  COLORS.goldBg,
  goldBdr: COLORS.goldBorder,
  onGold:  COLORS.onGold,
  text:    COLORS.text,
  textSec: COLORS.textSec,
  muted:   COLORS.textMuted,
  success: COLORS.green,
  danger:  COLORS.red,
  dangerSoftBg: COLORS.dangerSoftBg,
  warning: COLORS.warning,
  info:    COLORS.blue,
};

// 79차: [버그 수정] new Date(end)(UTC 자정 파싱) vs new Date()(로컬/KST 현재시각)를
// 그대로 빼서, 계약 만료일 당일 새벽(자정~오전9시 KST) 몇 시간 동안 하루 밀린 D-Day가
// 표시될 수 있었음(전수 정독으로 발견) — 이 세션이 반복적으로 고쳐온 UTC vs 로컬 혼동과
// 같은 유형. end/오늘 둘 다 순수 날짜 문자열이므로 Date.UTC로 동일하게 파싱해 시간대
// 영향 자체를 없앰(달력일 차이만 계산).
const dDayLabel = (end) => {
  if (!end) return '-';
  const [ey, em, ed] = end.split('-').map(Number);
  const [ty, tm, td] = todayStr().split('-').map(Number);
  const diff = Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(ty, tm - 1, td)) / 86400000);
  if (diff<0) return '만료'; if (diff===0) return 'D-DAY'; return `D-${diff}`;
};

// ─────────────────────────────────────────────────────────────────────────────
export default function SuperAdminScreen() {
  const { user } = useAuth();
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefresh]    = useState(false);
  const [stores,       setStores]       = useState([]);
  const [reservations, setReservations] = useState([]);
  const [users,        setUsers]        = useState([]);
  const [pushStats,    setPushStats]    = useState([]); // 78차: 알림 발송 신뢰성 모니터링

  // 38차: maxThemes 수정 모달 상태
  const [editingStore,    setEditingStore]    = useState(null); // { id, ownerName, maxThemes }
  const [maxThemesInput,  setMaxThemesInput]  = useState('');
  const [savingMaxThemes, setSavingMaxThemes] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      // 오늘 fork 재검토(Firestore 읽기비용 전수점검)로 발견/수정: 이 화면은 GMV 6개월
      // 추이/이달 통계만 쓰는데(DashboardTab의 gmvTrend가 최대 6개월치) 필터 없이
      // reservations 컬렉션 전체(플랫폼 생성 이래 모든 예약)를 매번 읽고 있었음 —
      // 6개월 이전 데이터는 이 화면 어디에도 안 쓰이므로 쿼리 단계에서 잘라냄.
      const gmvCutoff = (() => {
        const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 5);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      })();
      const [storeSnap, reservSnap, userSnap, pushStatsSnap] = await Promise.all([
        getDocs(collection(db,'stores')),
        getDocs(query(collection(db,'reservations'), where('date', '>=', gmvCutoff))),
        getDocs(collection(db,'users')),
        getDocs(query(collection(db,'pushStats'), orderBy('date','desc'), limit(14))),
      ]);
      setStores(storeSnap.docs.map(d=>({id:d.id,...d.data()})));
      setReservations(reservSnap.docs.map(d=>({id:d.id,...d.data()})));
      setUsers(userSnap.docs.map(d=>({id:d.id,...d.data()})));
      setPushStats(pushStatsSnap.docs.map(d=>({id:d.id,...d.data()})));
    } catch(e){ /* 무음 처리 */ }
    finally { setLoading(false); }
  }, []);

  useEffect(()=>{ loadAll(); },[loadAll]);
  const onRefresh = async ()=>{ setRefresh(true); await loadAll(); setRefresh(false); };

  // 38차: maxThemes 저장
  const handleSaveMaxThemes = async () => {
    const val = parseInt(maxThemesInput);
    if (isNaN(val) || val < 1) { showAlert('오류', '1 이상의 숫자를 입력해주세요.'); return; }
    setSavingMaxThemes(true);
    try {
      await updateDoc(doc(db, 'stores', editingStore.id), { maxThemes: val });
      // 80차 후속: [버그 수정 — write-then-display 재감사로 발견, SuperAdminStoresScreen.js의
      // 같은 유형(handleSaveNote) 옆에서 같은 패턴으로 발견] 이 화면 자체는 setStores로
      // 로컬 갱신되지만 12초 공용 캐시를 안 비워 그 창 안에 다른 화면(테마 추가 시
      // maxThemes 상한을 확인하는 곳 등)이 낡은 값을 볼 수 있었음.
      invalidateStoreCache();
      logContractChange(editingStore.id, 'maxThemes',
        { maxThemes: editingStore.maxThemes ?? null }, { maxThemes: val }, user?.uid
      ).catch(() => {});
      setStores(prev => prev.map(s => s.id === editingStore.id ? { ...s, maxThemes: val } : s));
      setEditingStore(null);
      showAlert('저장 완료', `${editingStore.ownerName}의 최대 테마 수가 ${val}개로 변경됐어요.`);
    } catch (e) {
      showAlert('오류', e.message);
    } finally { setSavingMaxThemes(false); }
  };

  // 73차: 로딩 중에도 AppHeader 유지 (탭 전환마다 헤더가 잠깐 사라지던 문제 수정)
  if (loading) return (
    <View style={s.container}>
      <AppHeader rightElement={<RoleBadge role="super" />} />
      <LoadingOverlay />
    </View>
  );

  return (
    <View style={s.container}>
      <AppHeader rightElement={<RoleBadge role="super" />} />

      <ScrollView style={s.body}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.gold}/>}>
        <DashboardTab stores={stores} reservations={reservations} users={users} pushStats={pushStats}
          onEditMaxThemes={(store) => { setEditingStore(store); setMaxThemesInput(String(store.maxThemes || '')); }} />
        <View style={{height:60}}/>
      </ScrollView>

      {/* 38차: maxThemes 수정 모달 */}
      {editingStore && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setEditingStore(null)}>
          <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.7)',justifyContent:'center',padding:32}}>
            <View style={{backgroundColor:C.card,borderRadius:RADIUS.lg,borderWidth:1,borderColor:C.goldBdr,overflow:'hidden'}}>
              <AppHeader isModal centerElement={
                <IconLabel Icon={ContractIcon} size={15} color={C.text} textStyle={{color:C.text,fontSize:15,fontWeight:'800'}}>계약 테마 수 수정</IconLabel>
              }/>
              <View style={{padding:20}}>
              <Text style={{color:C.muted,fontSize:12,marginBottom:16}}>
                {editingStore.ownerName} — 이 수를 초과하면 매장관리자가 테마를 추가할 수 없어요.
              </Text>
              <Text style={{color:C.textSec,fontSize:12,marginBottom:6}}>최대 테마 수 *</Text>
              <TextInput
                style={{backgroundColor:C.surface,borderWidth:1,borderColor:C.border,
                  borderRadius:RADIUS.sm,padding:12,color:C.text,fontSize:16,
                  fontWeight:'700',marginBottom:16}}
                value={maxThemesInput}
                onChangeText={setMaxThemesInput}
                keyboardType="number-pad"
                placeholder="예: 5"
                placeholderTextColor={C.muted}
              />
              <View style={{flexDirection:'row',gap:10}}>
                <GradientButton
                  label={savingMaxThemes ? '저장 중...' : '저장'}
                  onPress={handleSaveMaxThemes}
                  disabled={savingMaxThemes}
                  style={{flex:1}}
                  textStyle={{fontSize:14}}
                />
                <SecondaryButton label="취소" onPress={() => setEditingStore(null)} style={{flex:1}} textStyle={{fontSize:14}} />
              </View>
              </View>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 대시보드
// ══════════════════════════════════════════════════════════════════════════════
function DashboardTab({ stores, reservations, users, pushStats, onEditMaxThemes }) {
  const [drilldown, setDrilldown] = useState(null);
  // 79차: [버그 수정] 소프트삭제(isDeleted)된 지점/테마까지 그대로 세고 있었음(전수
  // 정독으로 발견) — feeService.js의 getStoreThemeCount()(이미 이 세션에서 정본으로
  // 확인된 구현)는 이 필터를 이미 하고 있는데, 이 화면은 그 헬퍼를 안 쓰고 직접
  // 재구현하면서 필터를 빠뜨려 플랫폼 KPI가 실제보다 부풀려져 있었음.
  const totalBranches = stores.reduce((s,st)=>s+(st.branches||[]).filter(b=>b.isDeleted!==true).length,0);
  const totalThemes   = stores.reduce((s,st)=>s+getStoreThemeCount(st), 0);

  const thisMonth    = thisMonthStr();
  const sevenDaysAgo = new Date(Date.now()-7*24*60*60*1000).toISOString();

  const activeRes     = reservations.filter(r=>!r.cancelled&&r.revenueDuplicate!==true); // 78차 44차: 모집 일괄예약 매출 중복집계 방지
  const thisMonthRes  = activeRes.filter(r=>r.date?.startsWith(thisMonth));
  const newUsersCount = users.filter(u=>u.createdAt && u.createdAt>sevenDaysAgo).length;

  // 78차 32차: GMV 대시보드 — 플랫폼 전체 거래액(모든 매장 예약 매출 합계). 매출 계산
  // 기준은 StoreAdminScreen.js의 DashboardTab/StatsTab과 동일하게 결제/입금 확인 전
  // (paymentStatus:'pending') 건은 제외 — 아직 확정 안 된 금액까지 GMV에 잡히면 과대
  // 표시됨. 최근 6개월 추이도 같은 기준으로 계산(월별 billings 문서가 아니라 실제
  // reservations를 직접 집계 — billings는 수수료 계산용 요약 문서라 원 매출 총액과는
  // 다른 개념).
  const gmvRes = activeRes.filter(r => r.paymentStatus !== 'pending');
  const thisMonthGmv = gmvRes.filter(r => r.date?.startsWith(thisMonth)).reduce((s,r)=>s+(r.price||0),0);
  // 78차: toISOString()(UTC) 대신 로컬 getFullYear/getMonth를 직접 읽어 조립 — 자정 근처
  // 하루 밀림 버그 방지(dateUtils.js 참고 패턴과 동일한 이유).
  const gmvMonthOptions = Array.from({length:6},(_,i)=>{
    const d=new Date(); d.setDate(1); d.setMonth(d.getMonth()-i);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  }).reverse();
  const gmvTrend = gmvMonthOptions.map(m => ({
    month: m,
    gmv: gmvRes.filter(r=>r.date?.startsWith(m)).reduce((s,r)=>s+(r.price||0),0),
  }));
  const prevMonthGmv = gmvTrend[gmvTrend.length-2]?.gmv || 0;
  const gmvDiffRate = prevMonthGmv>0 ? Math.round(((thisMonthGmv-prevMonthGmv)/prevMonthGmv)*100) : null;
  const maxGmv = Math.max(...gmvTrend.map(t=>t.gmv), 1);

  // 78차: 알림 신뢰성 모니터링 — 최근 7일 합계로 발송 실패율 표시(noToken/deviceNotRegistered는
  // "그 유저에게 못 보냈다"는 뜻이라 실패에 포함, deviceNotRegistered는 error에 이미 포함된
  // 하위 사유라 합계에 중복으로 더하지 않음).
  const push7d = pushStats.slice(0, 7);
  const pushOk = push7d.reduce((s,p)=>s+(p.ok||0),0);
  const pushFail = push7d.reduce((s,p)=>s+(p.error||0)+(p.noToken||0)+(p.fetchFailed||0),0);
  const pushTotal = pushOk + pushFail;
  const pushFailRate = pushTotal > 0 ? Math.round((pushFail/pushTotal)*100) : null;
  const pushColor = pushFailRate == null ? C.muted : pushFailRate >= 20 ? C.danger : pushFailRate >= 5 ? C.warning : C.success;

  const soonExpiring = stores.filter(st=>{
    if(!st.contractEnd) return false;
    const diff=Math.ceil((new Date(st.contractEnd)-new Date())/86400000);
    return diff>=0&&diff<=30;
  });

  // 역할별 카운트
  const superCount = users.filter(u=>u.adminRole==='super').length;
  const storeCount = users.filter(u=>u.adminRole==='store').length;
  const userCount  = users.filter(u=>u.adminRole!=='super'&&u.adminRole!=='store').length;

  // 78차 73차: themes/resMonth/newUsers는 지금까지 눌러도 아무 반응이 없었고(hint:null),
  // GMV 대시보드는 핵심지표 카드가 아니라 항상 펼쳐진 별도 섹션이었음 — 전부 다른 카드들과
  // 동일하게 "탭하면 상세" 방식으로 통일.
  const CARDS = [
    { key:'owners',   emoji:'🏢', Icon:BuildingIcon,        label:'계약 사업자', value:`${stores.length}개`,       color:C.gold,    hint:'목록 →' },
    { key:'branches', emoji:'🏪', Icon:MenuStoreIcon,       label:'계약 지점',   value:`${totalBranches}개`,       color:C.info,    hint:'목록 →' },
    { key:'themes',   emoji:'🔐', Icon:MenuEscapeIcon,      label:'등록 테마',   value:`${totalThemes}개`,         color:C.warning, hint:'목록 →' },
    { key:'resMonth', emoji:'📋', Icon:MenuReservationIcon, label:'이달 예약',   value:`${thisMonthRes.length}건`, color:C.success, hint:'현황 →' },
    { key:'users',    emoji:'👥', Icon:PeopleIcon,          label:'총 회원',     value:`${users.length}명`,        color:C.info,    hint:'목록 →' },
    { key:'newUsers', emoji:'🆕', label:'신규 가입(7일)', value:`${newUsersCount}명`,    color:C.success, hint:'목록 →' },
    { key:'gmv',      emoji:'💰', Icon:PriceIcon,           label:'GMV 대시보드', value:`${thisMonthGmv.toLocaleString()}원`, color:C.gold, hint:'대시보드 →' },
    { key:'pushHealth', emoji:'📡', Icon:MenuBellIcon, label:'알림 발송(7일)', value: pushFailRate==null ? '데이터 없음' : `실패율 ${pushFailRate}%`, color:pushColor, hint:'상세 →' },
  ];

  const DRILL_KEYS = ['owners','branches','themes','resMonth','users','newUsers','gmv','pushHealth'];
  const toggle = (key) => {
    if (!DRILL_KEYS.includes(key)) return;
    setDrilldown(p=>p===key?null:key);
  };

  // 역할 정렬: 총관리자 → 매장관리자 → 일반
  const roleOrder = r => r==='super'?0:r==='store'?1:2;
  const roleLabel = r => r==='super'?'총관리자':r==='store'?'매장관리자':'일반';
  const roleIcon  = r => r==='super'?AdminIcon:r==='store'?MenuStoreIcon:MenuNicknameIcon;
  const roleColor = r => r==='super'?C.danger:r==='store'?C.warning:C.muted;

  const sortedUsers = [...users].sort((a,b)=>roleOrder(a.adminRole)-roleOrder(b.adminRole));

  return (
    <View>
      <Text style={s.sectionTitle}>핵심 지표</Text>

      {/* 3열 카드 그리드 */}
      <View style={{flexDirection:'row',flexWrap:'wrap',gap:10,marginBottom:16}}>
        {CARDS.map(c=>(
          <TouchableOpacity
            key={c.key}
            style={[s.statCard,{borderTopColor:c.color},c.key==='resMonth'&&s.statCardHeadline,drilldown===c.key&&{borderColor:c.color,backgroundColor:C.surface}]}
            onPress={()=>toggle(c.key)}
            activeOpacity={c.hint?0.7:1}>
            {c.Icon ? <View style={{marginBottom:6}}><c.Icon size={22} color={c.color} /></View> : <Text style={{fontSize:22,marginBottom:6}}>{c.emoji}</Text>}
            <Text style={[s.statVal,{color:c.color}]}>{c.value}</Text>
            <Text style={s.statLbl}>{c.label}</Text>
            {c.hint&&(
              <Text style={[s.muted,{color:c.color,marginTop:6,fontSize:10,fontWeight:'600'}]}>
                {drilldown===c.key?'▲ 닫기':c.hint}
              </Text>
            )}
          </TouchableOpacity>
        ))}
      </View>

      {/* 78차 32차: GMV 대시보드 — 플랫폼 전체 거래액(모든 매장 예약 매출 합계).
          78차 73차: 항상 펼쳐져 있던 것 → 핵심지표의 "GMV 대시보드" 카드를 탭해야 보이도록 변경. */}
      {drilldown==='gmv' && (
        <View style={s.gmvCard}>
          <View style={{flexDirection:'row',alignItems:'flex-end',justifyContent:'space-between'}}>
            <View>
              <Text style={s.muted}>이번달 GMV</Text>
              <Text style={s.gmvHeadline}>{thisMonthGmv.toLocaleString()}원</Text>
            </View>
            {gmvDiffRate!==null && (
              <Text style={{color: gmvDiffRate>=0?C.success:C.danger, fontSize:13, fontWeight:'700'}}>
                전월 대비 {gmvDiffRate>=0?'+':''}{gmvDiffRate}%
              </Text>
            )}
          </View>
          <View style={{flexDirection:'row',alignItems:'flex-end',gap:6,marginTop:16,height:60}}>
            {gmvTrend.map(t=>(
              <View key={t.month} style={{flex:1,alignItems:'center',gap:4}}>
                <View style={{width:'100%',height:Math.max(4,(t.gmv/maxGmv)*48),backgroundColor:C.gold,borderRadius:3}} />
                <Text style={{color:C.muted,fontSize:9}}>{t.month.slice(5)}월</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* 드릴다운: 계약 사업자 */}
      {drilldown==='owners'&&(
        <View style={s.drillCard}>
          <IconLabel Icon={BuildingIcon} size={14} color={C.gold} textStyle={s.drillTitle}>계약 사업자 상세</IconLabel>
          {stores.length===0
            ? <Text style={s.muted}>등록된 사업자가 없습니다.</Text>
            : stores.map(store=>{
              const dd=dDayLabel(store.contractEnd);
              const urgent=dd!=='-'&&dd!=='만료'&&parseInt(dd.replace('D-',''))<=30;
              // 79차: [버그 수정] 위와 동일 이유 — 소프트삭제 필터 누락으로 "테마 N/M개
              // ⚠️ 초과" 경고가 실제로는 한도를 안 넘겼는데도 뜰 수 있었음.
              const totalThemeCount = getStoreThemeCount(store);
              const maxT = store.maxThemes ?? null;
              const overLimit = maxT !== null && totalThemeCount > maxT;
              return (
                <View key={store.id} style={[s.drillRow, overLimit && {backgroundColor:C.dangerSoftBg}]}>
                  <View style={{flex:1}}>
                    <Text style={s.drillName}>{store.ownerName}</Text>
                    <Text style={s.muted}>지점 {store.branches?.length||0}개 · {store.contractStart||'-'} ~ {store.contractEnd||'-'}</Text>
                    {/* 38차: maxThemes 표시 */}
                    <View style={{flexDirection:'row',alignItems:'center',gap:6,marginTop:4}}>
                      <Text style={{color: overLimit ? C.danger : C.muted, fontSize:11}}>
                        테마 {totalThemeCount}/{maxT ?? '∞'}개
                      </Text>
                      {overLimit && <IconLabel Icon={WarningIcon} size={10} color={C.danger} textStyle={{color:C.danger,fontSize:10,fontWeight:'700'}}>초과</IconLabel>}
                    </View>
                  </View>
                  <View style={{alignItems:'flex-end',gap:6}}>
                    <Text style={{color:urgent?C.danger:C.warning,fontWeight:'700',fontSize:13}}>{dd}</Text>
                    {/* 38차: 계약 테마 수 수정 버튼 */}
                    <SecondaryButton
                      label={`📋 ${maxT ? `계약 ${maxT}개` : '한도 설정'}`}
                      onPress={()=>onEditMaxThemes(store)}
                      style={{paddingHorizontal:8,paddingVertical:3}}
                      textStyle={{fontSize:10}}
                    />
                  </View>
                </View>
              );
            })}
        </View>
      )}

      {/* 드릴다운: 계약 지점 */}
      {/* 79차: [버그 수정] 위 핵심지표 카드의 totalBranches는 이미 isDeleted 필터링됐는데
          (79차 수정), 이 드릴다운은 필터 없이 그대로 나열해 소프트삭제된 지점이 "등록됨"
          으로 뜨고 테마 개수도 삭제된 테마까지 포함돼 카드 숫자와 상세 목록이 어긋났음
          (전수 정독으로 발견) — 헤드라인 집계와 동일 기준으로 통일. */}
      {drilldown==='branches'&&(
        <View style={s.drillCard}>
          <IconLabel Icon={MenuStoreIcon} size={14} color={C.gold} textStyle={s.drillTitle}>계약 지점 상세</IconLabel>
          {stores.flatMap(store=>(store.branches||[]).filter(b=>b.isDeleted!==true).map((b,bi)=>{
            // 80차 후속: 방탈출/체험형 구분해서 볼 수 있게 — 단일 "테마 N개" 대신 형식별 개수
            const activeThemes=(b.themes||[]).filter(t=>t.isDeleted!==true);
            const roomN=activeThemes.filter(t=>(t.bookingFormat||'room')!=='show').length;
            const showN=activeThemes.filter(t=>t.bookingFormat==='show').length;
            const themeSummary=[roomN>0&&`방탈출 ${roomN}개`,showN>0&&`체험형 ${showN}개`].filter(Boolean).join(' · ')||'테마 없음';
            return (
            <View key={`${store.id}_${bi}`} style={s.drillRow}>
              <View style={{flex:1}}>
                <Text style={s.drillName}>{b.branchName}</Text>
                <Text style={s.muted}>{store.ownerName} · {[b.city,b.district].filter(Boolean).join(' ')||'위치 미등록'} · {themeSummary}</Text>
              </View>
              <Text style={[s.muted,{color:C.success}]}>등록됨</Text>
            </View>
            );
          }))}
        </View>
      )}

      {/* 드릴다운: 회원 목록 — 총관리자 → 매장관리자 → 일반 순서 */}
      {drilldown==='users'&&(
        <View style={s.drillCard}>
          <IconLabel Icon={PeopleIcon} size={14} color={C.gold} textStyle={s.drillTitle}>회원 목록 (역할 순)</IconLabel>

          {/* 역할별 요약 */}
          <View style={{flexDirection:'row',gap:8,marginBottom:14}}>
            {[
              {label:'총관리자', count:superCount, color:C.danger},
              {label:'매장관리자', count:storeCount, color:C.warning},
              {label:'일반 사용자', count:userCount, color:C.muted},
            ].map(item=>(
              <View key={item.label}
                style={{flex:1,backgroundColor:item.color+'15',borderRadius:8,
                  borderWidth:1,borderColor:item.color+'40',padding:10,alignItems:'center'}}>
                <Text style={{color:item.color,fontSize:16,fontWeight:'900'}}>{item.count}</Text>
                <Text style={{color:item.color,fontSize:10,fontWeight:'600',marginTop:2}}>{item.label}</Text>
              </View>
            ))}
          </View>

          {sortedUsers.slice(0,30).map(u=>(
            <View key={u.id} style={s.drillRow}>
              <View style={{flex:1}}>
                <Text style={s.drillName}>{u.nickname||'(닉네임없음)'}</Text>
                <Text style={s.muted}>{u.email||u.id}</Text>
              </View>
              <View style={{alignItems:'flex-end',gap:3}}>
                <View style={{backgroundColor:roleColor(u.adminRole)+'18',borderRadius:6,borderWidth:1,
                  borderColor:roleColor(u.adminRole)+'50',paddingHorizontal:7,paddingVertical:2}}>
                  <IconLabel Icon={roleIcon(u.adminRole)} size={10} color={roleColor(u.adminRole)} gap={3}
                    textStyle={{color:roleColor(u.adminRole),fontSize:10,fontWeight:'700'}}>
                    {roleLabel(u.adminRole)}
                  </IconLabel>
                </View>
                <Text style={[s.muted,{fontSize:10}]}>
                  {u.createdAt ? new Date(u.createdAt).toLocaleDateString('ko-KR') : '-'}
                </Text>
              </View>
            </View>
          ))}
          {sortedUsers.length > 30 && (
            <Text style={[s.muted,{textAlign:'center',marginTop:10}]}>
              외 {sortedUsers.length-30}명 — 유저·신고 탭에서 전체 조회 가능
            </Text>
          )}
        </View>
      )}

      {/* 계약 만료 임박 경고 */}
      {soonExpiring.length>0&&(
        <View style={[s.card,{borderLeftWidth:3,borderLeftColor:C.danger,marginTop:4}]}>
          <IconLabel Icon={ContractExpiringIcon} size={14} color={C.danger} textStyle={[s.sectionTitle,{color:C.danger,marginTop:0}]}>계약 만료 임박 (30일 이내)</IconLabel>
          {soonExpiring.map(st=>(
            <View key={st.id} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:6}}>
              <Text style={s.drillName}>{st.ownerName}</Text>
              <Text style={{color:C.danger,fontWeight:'700'}}>{dDayLabel(st.contractEnd)}</Text>
            </View>
          ))}
        </View>
      )}

      {/* 78차 73차: "이달 예약" 핵심지표 카드를 탭해야 보이도록 변경(기존엔 항상 노출) */}
      {drilldown==='resMonth' && (
        <View style={[s.card,{borderLeftWidth:3,borderLeftColor:C.success}]}>
          <IconLabel Icon={MenuReservationIcon} size={14} color={C.success} textStyle={[s.sectionTitle,{marginTop:0,color:C.success}]}>이달 예약 현황</IconLabel>
          {thisMonthRes.length === 0 ? (
            <Text style={s.muted}>이번 달 예약이 아직 없습니다.</Text>
          ) : (
            <View style={{flexDirection:'row',justifyContent:'space-around'}}>
              {[
                {l:'총 예약',  v:`${thisMonthRes.length}건`,                                        c:C.text},
                {l:'확정',    v:`${thisMonthRes.filter(r=>r.paymentStatus==='confirmed').length}건`, c:C.success},
                {l:'대기',    v:`${thisMonthRes.filter(r=>r.paymentStatus==='pending').length}건`,   c:C.warning},
              ].map(item=>(
                <View key={item.l} style={{alignItems:'center'}}>
                  <Text style={{fontSize:18,fontWeight:'800',color:item.c}}>{item.v}</Text>
                  <Text style={s.muted}>{item.l}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* 78차 73차: 등록 테마 드릴다운 신규 — 매장·지점별 테마 수 목록 */}
      {/* 79차: [버그 수정] 위 계약 지점 드릴다운과 동일한 이유로 isDeleted 필터 추가 —
          핵심지표의 totalThemes(getStoreThemeCount)와 기준 통일. */}
      {drilldown==='themes' && (
        <View style={s.drillCard}>
          <IconLabel Icon={MenuEscapeIcon} size={14} color={C.gold} textStyle={s.drillTitle}>등록 테마 상세</IconLabel>
          {stores.length===0
            ? <Text style={s.muted}>등록된 매장이 없습니다.</Text>
            : stores.flatMap(store => (store.branches||[]).filter(b=>b.isDeleted!==true).map((b,bi) => {
              const activeThemes = (b.themes||[]).filter(t=>t.isDeleted!==true);
              // 80차 후속: 방탈출/체험형 구분 — 체험형 테마명 뒤에 "(체험형)" 표기,
              // 우측 개수도 형식별로 분리 표시
              const roomN=activeThemes.filter(t=>(t.bookingFormat||'room')!=='show').length;
              const showN=activeThemes.filter(t=>t.bookingFormat==='show').length;
              const countSummary=[roomN>0&&`방탈출 ${roomN}`,showN>0&&`체험형 ${showN}`].filter(Boolean).join(' · ')||'0개';
              return (
              <View key={`${store.id}_${bi}`} style={s.drillRow}>
                <View style={{flex:1}}>
                  <Text style={s.drillName}>{store.ownerName} · {b.branchName}</Text>
                  <Text style={s.muted} numberOfLines={1}>
                    {activeThemes.map(t=>t.bookingFormat==='show'?`${t.name}(체험형)`:t.name).join(', ') || '등록된 테마 없음'}
                  </Text>
                </View>
                <Text style={[s.muted,{color:C.warning,fontWeight:'700'}]}>{countSummary}</Text>
              </View>
              );
            }))}
        </View>
      )}

      {/* 78차 73차: 신규 가입(7일) 드릴다운 신규 */}
      {drilldown==='newUsers' && (
        <View style={s.drillCard}>
          <IconLabel Icon={PeopleIcon} size={14} color={C.gold} textStyle={s.drillTitle}>최근 7일 신규 가입</IconLabel>
          {users.filter(u=>u.createdAt && u.createdAt>sevenDaysAgo).length===0
            ? <Text style={s.muted}>최근 7일간 신규 가입이 없습니다.</Text>
            : users.filter(u=>u.createdAt && u.createdAt>sevenDaysAgo)
              .sort((a,b)=>(b.createdAt||'').localeCompare(a.createdAt||''))
              .map(u=>(
                <View key={u.id} style={s.drillRow}>
                  <View style={{flex:1}}>
                    <Text style={s.drillName}>{u.nickname||'(닉네임없음)'}</Text>
                    <Text style={s.muted}>{u.email||u.id}</Text>
                  </View>
                  <View style={{alignItems:'flex-end',gap:3}}>
                    <View style={{backgroundColor:roleColor(u.adminRole)+'18',borderRadius:6,borderWidth:1,
                      borderColor:roleColor(u.adminRole)+'50',paddingHorizontal:7,paddingVertical:2}}>
                      <Text style={{color:roleColor(u.adminRole),fontSize:10,fontWeight:'700'}}>{roleLabel(u.adminRole)}</Text>
                    </View>
                    <Text style={[s.muted,{fontSize:10}]}>{new Date(u.createdAt).toLocaleDateString('ko-KR')}</Text>
                  </View>
                </View>
              ))}
        </View>
      )}

      {/* 78차: 알림 신뢰성 모니터링 — Firebase 콘솔 로그를 안 뒤져도 일자별 발송 성공/실패
          추이를 여기서 바로 확인 가능. onNotificationCreated가 매일 하나씩 남기는 pushStats
          문서(ok/error/noToken/deviceNotRegistered/fetchFailed)를 그대로 보여줌. */}
      {drilldown==='pushHealth' && (
        <View style={s.drillCard}>
          <IconLabel Icon={PriceIcon} size={14} color={C.gold} textStyle={s.drillTitle}>알림 발송 현황 (최근 14일)</IconLabel>
          <Text style={[s.muted,{marginBottom:10}]}>
            success: 알림이 정상 전달됨 / error: 발송 실패(기기 미등록 포함) / noToken: 유저에게 등록된 기기 토큰이 없음
          </Text>
          {pushStats.length===0 ? (
            <Text style={s.muted}>아직 집계된 발송 기록이 없습니다(오늘 이후 발송분부터 쌓입니다).</Text>
          ) : pushStats.map(p => {
            const ok = p.ok||0, error = p.error||0, noToken = p.noToken||0, fetchFailed = p.fetchFailed||0;
            const total = ok+error+noToken+fetchFailed;
            const failRate = total>0 ? Math.round(((error+noToken+fetchFailed)/total)*100) : 0;
            const rowColor = total===0 ? C.muted : failRate>=20 ? C.danger : failRate>=5 ? C.warning : C.success;
            return (
              <View key={p.id} style={s.drillRow}>
                <View style={{flex:1}}>
                  <Text style={s.drillName}>{p.date}</Text>
                  <Text style={s.muted}>성공 {ok} · 실패 {error+noToken+fetchFailed}{noToken>0?` (토큰없음 ${noToken})`:''}{p.deviceNotRegistered>0?` · 기기미등록 ${p.deviceNotRegistered}`:''}</Text>
                </View>
                <Text style={{color:rowColor,fontSize:13,fontWeight:'800'}}>{total===0?'-':`${failRate}%`}</Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container:    {flex:1,backgroundColor:C.bg},
  body:         {flex:1,padding:16},
  sectionTitle: {fontSize:14,fontWeight:'700',color:C.gold,marginBottom:10,marginTop:6,letterSpacing:0.5,fontFamily:FONTS.display},
  muted:        {color:C.muted,fontSize:12},
  card:         {backgroundColor:C.card,borderRadius:RADIUS.md,borderWidth:1,borderColor:C.border,padding:16,marginBottom:12,...SHADOW.card},
  statCard:     {width:'30.5%',backgroundColor:C.card,borderRadius:16,padding:15,borderTopWidth:3,alignItems:'center',borderWidth:1,borderColor:C.border,...SHADOW.card},
  statCardHeadline: {backgroundColor:'rgba(216,174,82,0.08)',borderColor:'rgba(216,174,82,0.2)'},
  statVal:      {fontSize:18,fontWeight:'800',marginBottom:3},
  statLbl:      {color:C.muted,fontSize:11,textAlign:'center'},
  gmvCard:      {backgroundColor:C.card,borderRadius:RADIUS.md,borderWidth:1,borderColor:C.border,padding:16,marginBottom:16,...SHADOW.card},
  gmvHeadline:  {color:C.gold,fontSize:22,fontWeight:'900',marginTop:2,fontFamily:FONTS.display},
  drillCard:    {backgroundColor:C.surface,borderRadius:RADIUS.md,borderWidth:1,borderColor:C.goldBdr,padding:16,marginBottom:16,...SHADOW.glow},
  drillTitle:   {fontSize:14,fontWeight:'700',color:C.gold,marginBottom:12,fontFamily:FONTS.display},
  drillRow:     {flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingVertical:10,borderBottomWidth:1,borderBottomColor:C.border},
  drillName:    {color:C.text,fontWeight:'600',fontSize:14},
});